import { ITestCase } from "./ITestCase";
import { getGarconUrl, randomString } from "../Utils";
import { Features, Services } from "./Modules";
import { KaldunTicketsClient } from "../generated/submodules/garcon-api/TicketsServiceClientPb";
import { User, checkGarconUrl, checkTicket, connectSignalling, generateNUsers, tryFetchNTickets, assertAllSettled } from "./Utils";
import KaldunTicketSubject from "../client/TicketSubject";
import { Ticket } from "../generated/submodules/garcon-api/tickets_pb";
import { IcePolicy } from "../client/Domain";
import RoomSignaling from "../client/RoomSignaling";
import RoomSession from "../client/RoomSession";
import { MemberInfo, MemberStatus } from "../client/Messages";
import { Subscription } from "rxjs";
import Delay from "../client/Delay";
import { AwaitQueue } from "awaitqueue";



export default class FourUsersJoinToLiveParty implements ITestCase {
    readonly tags: Set<string> = new Set([
        Features.LiveParty,
        Services.Garcon,
        Services.Kvashanina,
        Services.Kaldun,
        Services.Mediasoup,
    ]);
    readonly name: string = "Four users join to live party";

    async callback(assert: Assert): Promise<void> {
        const garconUrl = checkGarconUrl(assert, await getGarconUrl());

        const client = new KaldunTicketsClient(garconUrl);

        const roomId = randomString();

        const users = generateNUsers(roomId, KaldunTicketSubject.BroadcastAndView, 4);

        const tickets = await tryFetchNTickets(client, { users });

        tickets.forEach(ticket => {
            checkTicket(assert, {
                ticket: ticket.ticket?.getTicket(),
                accountId: ticket.user.accountId,
                clientId: ticket.user.clientId,
                roomId,
                subject: KaldunTicketSubject.BroadcastAndView
            });
        })
        
        const verifiedTickets = tickets as Array<{ ticket: Ticket, user: User }>;

        await joinToLiveParty(assert, verifiedTickets, roomId);
    }
}

async function joinToLiveParty(
    assert: Assert,
    tickets: Array<{ ticket: Ticket, user: User }>,
    roomId: string,
): Promise<void> {
    const backgroundPromises: Promise<any>[] = [];

    async function connectSession(
        args: {
            signalling: RoomSignaling,
            user: User,
            ticket: Ticket,
        }
    ): Promise<{
        user: User,
        signalling: RoomSignaling,
        session: RoomSession,
        members: Array<MemberInfo>,
        subscription: Subscription,
    }> {
        const taskQueue = new AwaitQueue();

        const session = new RoomSession({
            roomId,
            icePolicy: IcePolicy.All,
            enableScreenShare: false,
            ticketSubject: KaldunTicketSubject.BroadcastAndView,
            iceServers: args.ticket.getIceServersList().flatMap(x => x.getIceServersList().map(y => {
                return {
                    credential: y.hasCredential() ? y.getCredential() : undefined,
                    username: y.hasUsername() ? y.getUsername() : undefined,
                    urls: y.getUrlsList(),
                    hostname: y.getHostname(),
                }
            })),
            maxCameraSendFrameRate: null,
            maxCameraSendBitrate: null,
        });

        const members : Array<MemberInfo> = [];

        const subscription = args.signalling.roomUpdatedNotification.subscribe(event => {
            const promise = taskQueue.push(async () => {
                if (event.room_updated.consumed_media_sdp_offer) {
                    assert.true(await session.applyConsumedSdpOffer(event.room_updated.consumed_media_sdp_offer), `User ${args.user.accountId} aplied consumed sdp answer in room = ${roomId}`);
                }
                members.push(...event.room_updated.members.filter(member => member.st === MemberStatus.Connected));
            });
            backgroundPromises.push(promise);
        });

        return await taskQueue.push(async () => {
            const offer = await session.createInitialOffer();
            const joinResult = await args.signalling.join({ offer });
        
            assert.true("join_room_v2" in joinResult, `User ${args.user.accountId} joined to room = ${roomId}`);
        
            if ("join_room_v2" in joinResult) {
                assert.true(await session.applyProducedSdpOffer(joinResult.join_room_v2.sdp_offer), `User ${args.user.accountId} aplied produced sdp offer in room = ${roomId}`);
                assert.true(await session.applyProducedSdpAnswer(joinResult.join_room_v2.sdp_answer), `User ${args.user.accountId} aplied produced sdp answer in room = ${roomId}`);
                if (joinResult.join_room_v2.sdp_server_offer) {
                    assert.true(await session.applyConsumedSdpOffer(joinResult.join_room_v2.sdp_server_offer), `User ${args.user.accountId} aplied consumed sdp answer in room = ${roomId}`);
                }
            } else {
                throw new Error(`User ${args.user.accountId} failed to join room ${roomId}: join_room_v2 not in result`);
            };

            return {
                user: args.user,
                signalling: args.signalling,
                session,
                members,
                subscription,
            };
        });
    } 

    const sessions : Array<{
        user: User,
        signalling: RoomSignaling,
        session: RoomSession,
        members: Array<MemberInfo>,
        subscription: Subscription,
    }> = [];
    try {
        for(let { ticket, user } of tickets) {
            const signalling = await connectSignalling(assert, ticket.getEndpoint(), user, KaldunTicketSubject.BroadcastAndView);
            if (!signalling) {
                return;
            }

            const sessionData = await connectSession({
                signalling,
                user,
                ticket,
            });

            sessions.push(sessionData);
        }

        await Delay(5000);

        for(let session of sessions) {
            const others = new Set(sessions.map(x => x.user.accountId).filter(x => x !== session.user.accountId));
            const members = new Set(session.members.map(x => x.acc));

            assert.deepEqual(members, others, `User ${session.user.accountId} has all members in room = ${roomId}`);
        }

        for(let session of sessions) {
            const leaveResult = await session.signalling.leave();
            assert.true("leave_room" in leaveResult, `User ${session.user.accountId} leaved from room = ${roomId}`);
        }
    } finally {
        await Promise.allSettled(backgroundPromises);

        for(let session of sessions) {
            session.subscription?.unsubscribe();
        }

        for(let session of sessions) {
            await session.session.terminate();
        }

        await assertAllSettled(backgroundPromises);
    }
}
